import React, { useCallback, useEffect, useState } from 'react';
import {
  applyTdsDetailSeo,
  downloadTdsPdf,
  fetchTdsPdfBlobUrl,
  findTdsBySlug,
  getTdsListTitle,
  openTdsPdfInNewTab,
} from '../../utils/tdsData';
import { fetchTdsCatalog, fetchTdsRecordBySlug } from '../../utils/tdsCatalogData';
import '../../pages/TdsPage.css';

const TdsPreviewModal = ({
  slug,
  initialRecord = null,
  onClose,
  openPdfDirect = false,
  autoDownload = false,
}) => {
  const [loading, setLoading] = useState(!initialRecord);
  const [record, setRecord] = useState(initialRecord);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [previewSrc, setPreviewSrc] = useState('');
  const [pdfLoading, setPdfLoading] = useState(false);

  const handleDownload = useCallback(async () => {
    if (!record?.pdfUrl || downloading) return;
    setDownloading(true);
    try {
      await downloadTdsPdf(record);
    } finally {
      setDownloading(false);
    }
  }, [record, downloading]);

  useEffect(() => {
    if (initialRecord) {
      setRecord(initialRecord);
      setLoading(false);
      setError(null);
      if (initialRecord.hasPdf) applyTdsDetailSeo(initialRecord);
      return undefined;
    }

    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        setError(null);

        const directMatch = await fetchTdsRecordBySlug(slug);
        let match = directMatch;
        if (!match) {
          const { records: list } = await fetchTdsCatalog({ pageNumber: 1, pageSize: 25 });
          match = findTdsBySlug(list, slug);
        }

        if (!match) {
          if (!cancelled) setError(`No TDS found for "${slug}".`);
          return;
        }

        if (!cancelled) {
          setRecord(match);
          applyTdsDetailSeo(match);
        }
      } catch (err) {
        console.error('TDS preview load error:', err);
        if (!cancelled) setError('Failed to load this TDS. Please try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (slug) load();
    return () => {
      cancelled = true;
    };
  }, [slug, initialRecord]);

  useEffect(() => {
    if (!record?.hasPdf || !record.pdfUrl) {
      setPreviewSrc('');
      setPdfLoading(false);
      return undefined;
    }

    let cancelled = false;
    let blobUrl = null;

    const loadPdf = async () => {
      setPdfLoading(true);
      setPreviewSrc('');
      try {
        blobUrl = await fetchTdsPdfBlobUrl(record.pdfUrl);
        if (!cancelled) setPreviewSrc(blobUrl);
      } catch (err) {
        console.warn('TDS PDF blob preview failed, using direct URL:', err);
        if (!cancelled) setPreviewSrc(record.pdfUrl);
      } finally {
        if (!cancelled) setPdfLoading(false);
      }
    };

    loadPdf();

    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [record?.pdfUrl, record?.hasPdf]);

  useEffect(() => {
    if (!record?.hasPdf || !record.pdfUrl) return undefined;
    if (openPdfDirect) {
      window.location.replace(record.pdfUrl);
      return undefined;
    }
    if (autoDownload) {
      downloadTdsPdf(record);
    }
    return undefined;
  }, [record, openPdfDirect, autoDownload]);

  useEffect(() => {
    document.body.classList.add('tds-pdf-view-active');
    return () => document.body.classList.remove('tds-pdf-view-active');
  }, []);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const fileLabel = record
    ? `${record.gradeName} ${record.codeName} TDS.pdf`.replace(/\s+/g, ' ')
    : 'TDS.pdf';

  return (
    <div
      className="tds-preview-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={record ? getTdsListTitle(record) : 'TDS File Preview'}
      onClick={onClose}
    >
      <div className="tds-preview-modal" onClick={(e) => e.stopPropagation()}>
        <div className="tds-preview-modal-header">
          <h1 className="tds-preview-modal-title">{record ? getTdsListTitle(record) : 'TDS File Preview'}</h1>
          <button
            type="button"
            className="tds-preview-close"
            onClick={onClose}
            aria-label="Close preview"
          >
            <i className="fas fa-times" aria-hidden />
          </button>
        </div>

        {loading && (
          <div className="tds-preview-state">
            <i className="fas fa-spinner fa-spin" aria-hidden />
            <p>Loading document…</p>
          </div>
        )}

        {!loading && error && (
          <div className="tds-preview-state">
            <i className="fas fa-circle-exclamation" aria-hidden />
            <p>{error}</p>
            <button type="button" className="tds-preview-action-btn" onClick={onClose}>
              Close
            </button>
          </div>
        )}

        {!loading && record && !record.hasPdf && (
          <div className="tds-preview-state">
            <i className="fas fa-file-circle-xmark" aria-hidden />
            <p>
              PDF not available for <strong>{record.codeName || 'this grade'}</strong>.
              <br />
              This material does not have an uploaded TDS file yet.
            </p>
            <button type="button" className="tds-preview-action-btn" onClick={onClose}>
              Close
            </button>
          </div>
        )}

        {!loading && record?.hasPdf && (
          <>
            <div className="tds-preview-doc-toolbar">
              <div className="tds-preview-doc-toolbar-left">
                <i className="fas fa-file-pdf" aria-hidden />
                <span className="tds-preview-filename" title={fileLabel}>
                  {fileLabel}
                </span>
              </div>
              <div className="tds-preview-doc-toolbar-right">
                <button
                  type="button"
                  className="tds-preview-icon-btn"
                  title="Open in new tab"
                  onClick={() => openTdsPdfInNewTab(record)}
                >
                  <i className="fas fa-external-link-alt" aria-hidden />
                </button>
                <button
                  type="button"
                  className="tds-preview-icon-btn"
                  title="Download PDF"
                  disabled={downloading}
                  onClick={handleDownload}
                >
                  <i className={`fas ${downloading ? 'fa-spinner fa-spin' : 'fa-download'}`} aria-hidden />
                </button>
              </div>
            </div>
            <div className="tds-preview-viewer">
              <aside className="tds-preview-thumbs" aria-hidden>
                <div className="tds-preview-thumb tds-preview-thumb--active">
                  <span>1</span>
                </div>
              </aside>
              <div className="tds-preview-frame">
                {pdfLoading && (
                  <div className="tds-preview-state tds-preview-state--inline">
                    <i className="fas fa-spinner fa-spin" aria-hidden />
                    <p>Loading PDF…</p>
                  </div>
                )}
                {!pdfLoading && previewSrc && (
                  <iframe
                    src={`${previewSrc}#page=1&view=FitH&toolbar=1&navpanes=0`}
                    title={getTdsListTitle(record)}
                    className="tds-pdf-iframe"
                  />
                )}
              </div>
            </div>
            <p className="tds-preview-viewer-hint">
              If the PDF does not appear, use{' '}
              <button type="button" className="tds-preview-inline-link" onClick={() => openTdsPdfInNewTab(record)}>
                Open in new tab
              </button>
              .
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default TdsPreviewModal;
